import { useMemo } from 'react';

import { useGetLiveMatchesQuery, useGetMeQuery } from '@/services/qgammonApi';
import { useQgammonRitmo, useQgammonVerdetto } from '@/hooks/useQgammonLista';
import { righeLive, type RigaLive } from '@/lib/qgammon/partiteLive';
import type { ListVerdict } from '@/lib/listState';

/**
 * Il ritmo della scheda Live: 15 s, come il «Live» di Clash Royale (`LiveMatchesPanel`). Qui il
 * punteggio cambia a ogni gioco, non a partita finita come nelle altre liste (60 s). Il backoff sui
 * fallimenti resta quello di tutte (`lib/pollingBackoff.ts`), e un 200 vuoto NON rallenta: e' proprio
 * il momento in cui una partita puo' comparire.
 */
export const QGAMMON_LIVE_POLL_MS = 15_000;

export interface QgammonLive {
  readonly verdict: ListVerdict;
  readonly righe: readonly RigaLive[];
  /** «Riprova» del caso 3 (`PanelNotice`): rilancia la lettura. */
  readonly riprova: () => void;
  /** Una lettura e' in volo: «Riprova» resta spento (con una richiesta pending `refetch` non fa niente). */
  readonly inVolo: boolean;
  /**
   * Righe a schermo, ma l'ultima lettura e' FALLITA (seguito della revisione di #437, 7c): RTK non
   * cancella `data` su un errore e `listVerdict` fa vincere le righe, quindi senza questo segnale
   * punteggi fermi e «da N min» che sale sembrerebbero dati vivi.
   */
  readonly nonAggiornato: boolean;
}

/**
 * Le partite in corso, con i CINQUE casi di un pannello senza righe (CLAUDE.md del repo, «Gli 8 tab
 * della Hub») — gli stessi mezzi di Classifica e Partite (`useQgammonRitmo`/`useQgammonVerdetto`).
 * Niente socket per l'elenco: il polling basta e non occupa un posto nel tetto dei socket.
 */
export function useQgammonLive(): QgammonLive {
  const ritmo = useQgammonRitmo(QGAMMON_LIVE_POLL_MS);
  const q = useGetLiveMatchesQuery(undefined, ritmo.opts);
  const me = useGetMeQuery();
  const myUserId = me.data?.user.id ?? null;
  const matches = q.data?.matches;
  const righe = useMemo(
    () => righeLive(Array.isArray(matches) ? matches : [], myUserId),
    [matches, myUserId],
  );
  const verdict = useQgammonVerdetto(ritmo, q, righe.length);
  return {
    verdict,
    righe,
    riprova: q.refetch,
    inVolo: q.isFetching,
    nonAggiornato: righe.length > 0 && ritmo.fallimenti > 0,
  };
}
